import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { defaultAction } from "@actions/defaultAction";
import { deleteAccount } from "@actions/accountActions";
import { OPEN_DELETE_ACCOUNT_MODAL } from "@constants/actionTypes";
import styled from "@emotion/styled";
import classNames from "classnames";
import CustomSubmitButton from "./CustomSubmitButton";

const PopUpContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 109;
  background-color: rgba(31, 32, 33, 0.9);

  @media (max-height: 705px) {
    position: absolute;
    height: 100%;
  }
`;

const Container = styled.div`
  width: 579px;
  border-radius: 30px;
  margin: 0 auto;
  margin-top: 100px;
  background-color: #ffffff;
  padding-bottom: 45px;

  @media (max-height: 925px) {
    margin-top: 8%;
  }
  @media (max-height: 800px) {
    margin-top: 4%;
    max-width: 544px;
  }

  @media (max-width: 579px) {
    width: 100%;
  }
  @media (max-width: 500px) {
    margin-top: 4%;
    max-width: 544px;
    height: auto;
    padding-bottom: 30px;
  }
`;

const CloseDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  color: #9b9b9b;
  padding-right: 30px;
  align-self: flex-start;
  padding-top: 20px;

  @media (max-width: 600px) {
    padding-right: 20px;
    padding-top: 15px;
  }

  @media (max-width: 375px) {
    padding-right: 15px;
    padding-top: 10px;
  }
`;

const CloseDivImage = styled.img`
  width: 17px;
`;

const CloseSpan = styled.span`
  cursor: pointer;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  width: 435px;
  margin: 0 auto;
  text-align: center;
  margin-top: -41px;

  @media (max-height: 800px) {
    margin-top: -50px;
  }

  @media (max-width: 500px) {
    margin-top: -50px;
    width: 85%;
  }
  @media (max-width: 450px) {
    margin-top: 0px;
    width: 90%;
  }
`;

const Header = styled.div`
  font-size: 30px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  text-align: center;
  color: #1f2021;
  margin-bottom: 18px;

  @media (max-width: 500px) {
    font-size: 26px;
  }
  @media (max-width: 375px) {
    font-size: 24px;
  }
`;

const ContentP = styled.p`
  font-size: 16px;
  line-height: 1.75;
  color: #777984;
  margin-bottom: 44px;

  @media (max-width: 350px) {
    font-size: 15px;
  }
`;

const ButtonDiv = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  grid-gap: 25px;
  margin-top: 43px;

  @media (max-width: 600px) {
    margin-top: 30px;
    grid-gap: 20px;
  }
  @media (max-width: 550px) {
    grid-gap: 15px;
  }
  @media (max-width: 500px) {
    margin-bottom: 20px;
    grid-gap: 12px;
  }
  @media (max-width: 375px) {
    grid-gap: 10px;
  }
`;

const DeleteAccountModal = ({
  closeDeleteAccountModal,
  deleteAccount,
  open
}) => {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      window.scrollTo(0, 0);
      if (window.innerHeight > 705 && window.innerWidth > 420) {
        document.body.style.overflow = "hidden";
      }
    }
    return () => {
      document.body.style.overflow = "initial";
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setLoading(false);
      setShowSuccess(false);
    }
  }, [open]);

  const handleDeleteAccount = async () => {
    if (loading) return;
    setLoading(true);
    const response = await deleteAccount();
    if (response === "success") {
      setLoading(false);
      setShowSuccess(true);
    } else {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (!loading) {
      closeDeleteAccountModal();
    }
  };

  if (!open) return null;

  return (
    <PopUpContainer className={classNames("animated", "fast", "fadeInUp")}>
      <Container>
        <CloseDiv>
          <CloseSpan onClick={closeModal}>
            <CloseDivImage
              src="https://cdn.cardcash.com/website/ui/elements/close.svg"
              aria-hidden="true"
              alt="close button"
              style={{ cursor: loading ? "not-allowed" : "pointer" }}
            />
          </CloseSpan>
        </CloseDiv>
        <Content>
          {showSuccess ? (
            <>
              <Header>Account Deleted</Header>
              <ContentP>
                Your account has been successfully deleted. You can no longer
                login or create a new account with this email address.
              </ContentP>
            </>
          ) : (
            <>
              <Header>Delete Account</Header>
              <ContentP>
                Are you sure you want to delete your account? You will no longer
                be able to access your account including your order history.
              </ContentP>
              <ButtonDiv>
                <CustomSubmitButton
                  styles={{ height: "72px", borderRadius: "36px" }}
                  action={() => {
                    handleDeleteAccount();
                  }}
                  buttonType="ORANGE_BUTTON"
                  className={"delete-account-modal-button"}
                  isLoading={loading}
                >
                  Delete Account
                </CustomSubmitButton>
                <CustomSubmitButton
                  styles={{ height: "72px", borderRadius: "36px" }}
                  action={closeModal}
                  buttonType={"WHITE_BUTTON_ORANGE_COLOR"}
                  className={"delete-account-modal-button"}
                  disabled={loading}
                >
                  Cancel
                </CustomSubmitButton>
              </ButtonDiv>
            </>
          )}
        </Content>
      </Container>
    </PopUpContainer>
  );
};

export default connect(
  state => ({
    open: state.popupsActions.showDeleteAccountModal
  }),
  dispatch => ({
    closeDeleteAccountModal: () =>
      dispatch(defaultAction(OPEN_DELETE_ACCOUNT_MODAL, false)),
    deleteAccount: () => dispatch(deleteAccount())
  })
)(DeleteAccountModal);
